import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Dropdown, Input, Switch } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useResponsive } from '@hooks/useResponsive';
import { AssetVersionCommentDto, AssetVersionCommentUserDto } from '@api/Api';
import { combineUserName } from '@helpers/combineUserName';
import { MdWidth } from '@components/Responsive';
import UserAvatar from '@components/UserAvatar';

import { ReactComponent as UserSvg } from '@assets/icons/user.svg';
import { ReactComponent as SortSvg } from '@assets/icons/sort-arrow.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as CommentsSvg } from '@assets/icons/comments.svg';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as FilterSvg } from '@assets/icons/filter.svg';
import { ReactComponent as CommentWithUserSvg } from '@assets/icons/comment-with-user.svg';
import { ReactComponent as CheckCircleFillSvg } from '@assets/icons/check-circle-fill.svg';

import './AccordionFilter.less';

export const DEFAULT_FILTER = 'All comments';
export const MY_COMMENT_FILTER = 'My comments';
export const RESOLVE_COMMENT_FILTER = 'resolved';
const DEFAULT_SORT = 'Date created';

interface IAccordionFilterCommentsProps {
  sortMenuText?: string;
  orderBy?: string;
  isUnresolved: boolean;
  onUnResolveHandler(value: boolean): void;
  onSortHandler?(key: string): void;
  onDropdownHandler(key: string[]): void;
  dropdownValue: string[];
  searchQuery?: string | null;
  onSearchOpenChange?(open: boolean): void;
  onSearchHandler?(value: string): void;
  commentsList: AssetVersionCommentDto[];
  assetType?: 'document' | 'audio';
  exportMenu?: ItemType[];
  onExportHandler?(key: string): void;
}

function AccordionFilterComments({
  sortMenuText = 'Sort',
  orderBy,
  dropdownValue,
  onSortHandler,
  onDropdownHandler,
  searchQuery,
  onSearchOpenChange,
  onSearchHandler,
  assetType,
  commentsList,
  exportMenu,
  isUnresolved,
  onUnResolveHandler,
  onExportHandler
}: IAccordionFilterCommentsProps) {
  const [isShowSearch, setIsShowSearch] = useState<boolean>(
    searchQuery !== undefined && searchQuery !== null
  );
  const [sortSelected, setSortSelected] = useState<string>(
    orderBy ?? DEFAULT_SORT
  );
  const [openKeysDropdown, setOpenKeysDropdown] = useState<string[]>([]);
  const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false);
  const [searchTextUser, setSearchTextUser] = useState<string>('');
  const { isMdWidth } = useResponsive();

  const onClearFilter = useCallback(() => {
    setOpenKeysDropdown([]);
    onDropdownHandler([DEFAULT_FILTER]);
    setIsOpenDropdown(false);
    onUnResolveHandler(false);
  }, [
    setOpenKeysDropdown,
    onDropdownHandler,
    setIsOpenDropdown,
    onUnResolveHandler
  ]);

  const sortMenu: ItemType[] = useMemo(() => {
    return [
      { label: 'Priority', key: 'Priority' },
      { label: 'Date created', key: 'Date created' },
      { label: 'Date modified', key: 'Date modified' },
      ...(assetType === 'document' ? [{ label: 'Page', key: 'Page' }] : []),
      ...(assetType === 'audio' ? [{ label: 'Timecode', key: 'Timecode' }] : [])
    ];
  }, [assetType]);

  const users = useMemo(
    () =>
      commentsList
        .filter((comment) => !!comment.user)
        .map((i) => i.user)
        .reduce((acc, user) => {
          if (!user) return acc;
          const exist = acc?.find((i) => i.id === user.id);
          if (exist) return acc;
          return [...acc, user];
        }, [] as AssetVersionCommentUserDto[]),
    [commentsList]
  );

  const hasSelectedUser = useMemo(() => {
    return users?.some((user) => dropdownValue.includes(user.id));
  }, [users, dropdownValue]);

  const commentsMenu: ItemType[] = useMemo(() => {
    let _users = users;
    if (searchTextUser) {
      _users = users.filter((user) => {
        const userName = combineUserName(user);
        return userName
          .toLocaleLowerCase()
          .includes(searchTextUser.toLocaleLowerCase());
      });
    }
    return [
      {
        key: 'groupFilter',
        label: 'Filter by',
        type: 'group'
      },
      {
        label: 'All comments',
        key: DEFAULT_FILTER,
        icon: <CommentsSvg />
      },
      {
        label: 'My comments',
        key: MY_COMMENT_FILTER,
        icon: <CommentWithUserSvg />
      },
      {
        label: (
          <Row align="middle" gutter={12}>
            <Col>Other commenters</Col>
            {hasSelectedUser && (
              <Col>
                <div className="b-dots" />
              </Col>
            )}
          </Row>
        ),
        key: 'usersGroup',
        popupClassName:
          'media_viewer_filter-menu media_viewer_submenu media_viewer_mobile-overlay',
        popupOffset: [12, 0],
        icon: <UserSvg />,
        children: [
          {
            key: 'usersGroupHead',
            label: (
              <Input
                prefix={<SearchSvg />}
                placeholder="Search by teammate"
                value={searchTextUser}
                className="media_viewer_filter-user-search"
                onChange={(e) => setSearchTextUser(e.target.value)}
              />
            ),
            type: 'group',
            className: 'media_viewer_filter-menu-users',
            children: _users.map((user) => {
              const userName = combineUserName(user);
              return {
                label: (
                  <div className="media_viewer_filter-user">
                    <UserAvatar
                      size={24}
                      isActive={true}
                      src={user?.picture.url || ''}
                      userEmail={user?.email || ''}
                      userName={userName}
                    />
                    {userName}
                  </div>
                ),
                key: `${user.id}`
              };
            })
          }
        ]
      },
      {
        type: 'divider'
      },
      {
        key: 'resolvedHead',
        label: 'Hide comments',
        type: 'group'
      },
      {
        label: (
          <Row
            gutter={12}
            justify="space-between"
            onClick={() => {
              onUnResolveHandler(!isUnresolved);
              setIsOpenDropdown(false);
            }}
          >
            <Col>Resolved</Col>
            <Col>
              <Switch checked={isUnresolved} />
            </Col>
          </Row>
        ),
        disabled: true,
        key: RESOLVE_COMMENT_FILTER,
        className: 'b-allow-action',
        icon: <CheckCircleFillSvg />
      },
      {
        type: 'divider'
      },
      {
        label: (
          <Button
            className="media_viewer_filter-menu-btn"
            onClick={onClearFilter}
          >
            Clear filters
          </Button>
        ),
        key: 'clear',
        disabled: true,
        className: 'b-allow-action',
        style: {
          background: 'transparent'
        }
      }
    ];
  }, [
    users,
    searchTextUser,
    hasSelectedUser,
    isUnresolved,
    onUnResolveHandler,
    setIsOpenDropdown,
    setSearchTextUser,
    onClearFilter
  ]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (onSearchHandler) {
      onSearchHandler(value);
    }
  };

  const updateItems = (arr: ItemType[]) => {
    if (isMdWidth) {
      const newArr = arr.map((item: any) =>
        item.children && item.type !== 'group'
          ? {
              ...item,
              children: [
                ...item.children,
                {
                  label: 'Cancel',
                  key: 'submenu-cancel'
                }
              ]
            }
          : item
      );
      return [
        ...newArr,
        {
          label: 'Cancel',
          key: 'cancel'
        }
      ];
    }
    return arr;
  };

  const searchValue = searchQuery === undefined ? undefined : searchQuery ?? '';

  const dropdownMenuText = useMemo(() => {
    if (!dropdownValue.length) return [DEFAULT_FILTER];
    return dropdownValue.map((key) => {
      const findUser = users.find((user) => user.id === key);
      if (findUser) return combineUserName(findUser);
      return key;
    });
  }, [dropdownValue, users]);

  useEffect(() => {
    const exist = sortMenu?.some((i) => i?.key === sortSelected);
    if (!exist && sortSelected !== DEFAULT_SORT) {
      onSortHandler?.(DEFAULT_SORT);
      setSortSelected(DEFAULT_SORT);
    }
  }, [sortSelected, sortMenu, onSortHandler, setSortSelected]);

  return (
    <div className="media_viewer_filter">
      {isShowSearch && (
        <div className="media_viewer_filter-search">
          <Input
            allowClear={{
              clearIcon: (
                <CrossSvg
                  onClick={() => {
                    setIsShowSearch(false);
                    if (onSearchOpenChange) onSearchOpenChange(false);
                  }}
                />
              )
            }}
            value={searchValue}
            onBlur={() => {
              if (onSearchHandler) onSearchHandler('');
              setIsShowSearch(false);
              if (onSearchOpenChange) onSearchOpenChange(false);
            }}
            onChange={(e) => {
              if (searchValue === e.target.value) return;
              onSearch(e);
            }}
            autoFocus={true}
            prefix={<SearchSvg />}
            className="media_viewer_filter-search-input"
            placeholder="Search comment"
          />
        </div>
      )}
      <Row gutter={isMdWidth ? 16 : 8} wrap={false} justify="space-between">
        <Col>
          <Dropdown
            menu={{
              selectable: true,
              selectedKeys: [sortSelected],
              onClick: ({ key }: { key: string }) => {
                if (onSortHandler && key.indexOf('cancel') < 0) {
                  onSortHandler(key);
                  setSortSelected(key);
                }
              },
              items: updateItems(sortMenu)
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_mobile-overlay"
            placement="bottomRight"
            trigger={['click']}
          >
            <Button className="media_viewer_btn media_viewer_filter-sort">
              {sortMenuText}
              <SortSvg className="b-white-icon" />
            </Button>
          </Dropdown>
        </Col>
        <Col>
          <Dropdown
            menu={{
              openKeys: openKeysDropdown,
              selectedKeys: dropdownValue,
              onSelect: (event) => {
                const isUsers = event.keyPath.includes('usersGroup');
                if (onDropdownHandler && event.key.indexOf('cancel') < 0) {
                  const selected = isUsers
                    ? [...dropdownValue, event.key].filter(
                        (i) => ![DEFAULT_FILTER, MY_COMMENT_FILTER].includes(i)
                      )
                    : [event.key];
                  onDropdownHandler(selected);
                  setIsOpenDropdown(isUsers);
                  return;
                }
                setIsOpenDropdown(false);
                setOpenKeysDropdown([]);
              },
              onDeselect: (event) => {
                const isUsers = event.keyPath.includes('users');
                if (onDropdownHandler && event.key.indexOf('cancel') < 0) {
                  const selected = dropdownValue.filter((i) => i !== event.key);
                  const defaultSelected = selected.length
                    ? selected
                    : [DEFAULT_FILTER];
                  onDropdownHandler(defaultSelected);
                  setIsOpenDropdown(isUsers);
                  return;
                }
                setIsOpenDropdown(false);
              },
              onOpenChange: (value) => {
                if (isMdWidth) setIsOpenDropdown(false);
                setOpenKeysDropdown(value);
              },
              items: updateItems(commentsMenu),
              multiple: true,
              triggerSubMenuAction: searchTextUser ? 'click' : 'hover',
              selectable: true
            }}
            prefixCls="media_viewer_menu"
            overlayClassName="media_viewer_filter-menu media_viewer_mobile-overlay"
            placement="bottomRight"
            trigger={['click']}
            open={
              (isOpenDropdown && !isMdWidth) ||
              (isOpenDropdown && isMdWidth && !openKeysDropdown.length)
            }
            onOpenChange={(value) => {
              setIsOpenDropdown(value);
              setSearchTextUser('');
            }}
          >
            <Button className="media_viewer_btn media_viewer_filter-comments">
              {dropdownMenuText[0]}{' '}
              {dropdownMenuText.length > 1 && (
                <span style={{ flex: 'none' }}>
                  , + {dropdownMenuText.length - 1}
                </span>
              )}
              <FilterSvg className="b-white-icon" style={{ flex: 'none' }} />
            </Button>
          </Dropdown>
        </Col>
        <Col flex="auto">
          <Button
            htmlType="button"
            onClick={() => {
              setIsShowSearch(true);
              if (onSearchOpenChange) onSearchOpenChange(true);
            }}
            className="media_viewer_btn b-only-icon"
          >
            <SearchSvg className="b-white-icon" />
          </Button>
        </Col>
        {exportMenu && (
          <MdWidth>
            <Col>
              <Dropdown
                menu={{
                  onClick: ({ key }: { key: string }) => {
                    if (onExportHandler && key.indexOf('cancel') < 0) {
                      onExportHandler(key);
                    }
                  },
                  selectable: false,
                  items: updateItems(exportMenu)
                }}
                prefixCls="media_viewer_menu"
                overlayClassName="media_viewer_accordion_head-overlay media_viewer_mobile-overlay"
                placement="bottomRight"
                trigger={['click']}
              >
                <Button className="media_viewer_btn b-only-icon">
                  <ActionSvg className="b-white-icon" />
                </Button>
              </Dropdown>
            </Col>
          </MdWidth>
        )}
      </Row>
    </div>
  );
}

export default AccordionFilterComments;
